import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import AuthService from '../../services/AuthService';
import PropertyService from '../../services/PropertyService';
import {
  PROPERTY_STATUSES, PROPERTY_CATEGORIES,
  getPropertyStatus, getPropertyCategory, fmtPrice, fmtSize,
} from '../../constants/propertyConstants';

// ── shared style tokens ──────────────────────────────────────────────────────
const card  = (extra = {}) => ({ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,.07)', ...extra });
const badge = (color, bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg });

export default function Property() {
  const toast     = useRef(null);
  const clientId  = AuthService.getLoggedInClientId();
  const navigate  = useNavigate();

  const [properties, setProperties] = useState([]);
  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [viewMode,   setViewMode]   = useState('grid'); // 'grid' | 'list'
  const [search,     setSearch]     = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterInventory, setFilterInventory] = useState('');
  const [filterProject,  setFilterProject]  = useState('');
  const [statusMenuId,   setStatusMenuId]   = useState(null); // inline status dropdown

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        PropertyService.getPropertiesByClientId(clientId),
        PropertyService.getStats(clientId),
      ]);
      setProperties(list);
      setStats(s);
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load properties' });
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // ── derived lists ──────────────────────────────────────────────────────────
  const projects = [...new Set(properties.map(p => p.projectName).filter(Boolean))];

  const filtered = properties.filter(p => {
    const q = search.toLowerCase();
    const matchQ = !q || [p.propertyName, p.projectName, p.ownerName, p.city, p.unitNumber]
      .some(v => (v || '').toLowerCase().includes(q));
    const matchS = !filterStatus   || p.propertyStatus   === filterStatus;
    const matchC = !filterCategory || p.propertyCategory === filterCategory;
    const matchP = !filterProject  || p.projectName      === filterProject;
    const matchI = !filterInventory || (p.inventorySource || 'Own') === filterInventory;
    return matchQ && matchS && matchC && matchP && matchI;
  });

  // ── quick status change ────────────────────────────────────────────────────
  const changeStatus = async (id, status) => {
    try {
      await PropertyService.updatePropertyStatus(id, status);
      setProperties(prev => prev.map(p => p.id === id ? { ...p, propertyStatus: status } : p));
      setStatusMenuId(null);
      toast.current?.show({ severity: 'success', summary: 'Updated', detail: `Status → ${status}`, life: 2000 });
      // refresh stats
      PropertyService.getStats(clientId).then(setStats).catch(() => {});
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Status update failed' });
    }
  };

  const deleteProperty = async (id) => {
    if (!window.confirm('Delete this property?')) return;
    try {
      await PropertyService.deleteProperty(id);
      setProperties(prev => prev.filter(p => p.id !== id));
      toast.current?.show({ severity: 'success', summary: 'Deleted', life: 2000 });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Delete failed' });
    }
  };

  // ── render helpers ─────────────────────────────────────────────────────────
  const StatCard = ({ label, val, color, icon }) => (
    <div style={{ ...card({ borderTop: `4px solid ${color}`, padding: 20, textAlign: 'center' }) }}>
      <i className={`fas ${icon}`} style={{ color, fontSize: 22, marginBottom: 8 }} />
      <p style={{ margin: 0, fontSize: 11, color: '#8898aa', fontWeight: 700, textTransform: 'uppercase' }}>{label}</p>
      <p style={{ margin: '4px 0 0', fontSize: 26, fontWeight: 800, color: '#1a1f36' }}>{val ?? '…'}</p>
    </div>
  );

  const StatusDropdown = ({ property }) => {
    const st = getPropertyStatus(property.propertyStatus);
    return (
      <div style={{ position: 'relative' }}>
        <button
          onClick={e => { e.stopPropagation(); setStatusMenuId(statusMenuId === property.id ? null : property.id); }}
          style={{ ...badge(st.color, st.bg), cursor: 'pointer', border: 'none', fontSize: 11 }}>
          <i className={st.icon} />{st.label} <i className="fas fa-chevron-down" style={{ fontSize: 9 }} />
        </button>
        {statusMenuId === property.id && (
          <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 100, background: '#fff', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.14)', padding: 6, minWidth: 170 }}>
            {PROPERTY_STATUSES.map(s => (
              <button key={s.id} onClick={e => { e.stopPropagation(); changeStatus(property.id, s.id); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 12px', background: s.id === property.propertyStatus ? s.bg : 'transparent', border: 'none', cursor: 'pointer', borderRadius: 7, fontSize: 13, color: s.color, fontWeight: s.id === property.propertyStatus ? 700 : 500 }}>
                <i className={s.icon} style={{ width: 14 }} />{s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const PropertyCard = ({ p }) => {
    const cat = getPropertyCategory(p.propertyCategory);
    return (
      <div onClick={() => navigate(`/property/${p.id}`)}
        style={{ ...card({ padding: 0, cursor: 'pointer', overflow: 'hidden', transition: 'transform .18s,box-shadow .18s' }),
          ':hover': { transform: 'translateY(-3px)' } }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
        {/* Image / placeholder */}
        <div style={{ height: 140, background: `linear-gradient(135deg,#5e72e4,#825ee4)`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <i className={cat.icon} style={{ fontSize: 48, color: 'rgba(255,255,255,.35)' }} />
          <div style={{ position: 'absolute', top: 10, left: 10 }}>
            <StatusDropdown property={p} />
          </div>
          {p.projectName && (
            <div style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(0,0,0,.5)', color: '#fff', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
              {p.projectName}{p.towerBlock ? ` · ${p.towerBlock}` : ''}{p.unitNumber ? ` · ${p.unitNumber}` : ''}
            </div>
          )}
          {p.inventorySource === 'ChannelPartner' && (
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <span style={{ ...badge('#825ee4', '#825ee424'), fontSize: 10 }}>CP</span>
            </div>
          )}
        </div>
        <div style={{ padding: 16 }}>
          <p style={{ margin: '0 0 4px', fontWeight: 800, color: '#1a1f36', fontSize: 15 }}>{p.propertyName}</p>
          <p style={{ margin: '0 0 10px', color: '#8898aa', fontSize: 12 }}>
            <i className="fas fa-map-marker-alt" style={{ marginRight: 4, color: '#5e72e4' }} />
            {[p.city, p.state].filter(Boolean).join(', ') || '—'}
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
            <span style={{ fontWeight: 800, color: '#5e72e4', fontSize: 16 }}>{fmtPrice(p.price)}</span>
            <span style={{ color: '#8898aa', fontSize: 12 }}>{fmtSize(p.size, p.sizeUnit)}</span>
          </div>
          {(p.bedrooms > 0 || p.bathrooms > 0) && (
            <div style={{ display: 'flex', gap: 12, marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f2f5' }}>
              {p.bedrooms > 0  && <span style={{ fontSize: 12, color: '#525f7f' }}><i className="fas fa-bed" style={{ marginRight: 4, color: '#5e72e4' }} />{p.bedrooms} Bed</span>}
              {p.bathrooms > 0 && <span style={{ fontSize: 12, color: '#525f7f' }}><i className="fas fa-bath" style={{ marginRight: 4, color: '#11cdef' }} />{p.bathrooms} Bath</span>}
              {p.facing && <span style={{ fontSize: 12, color: '#525f7f' }}><i className="fas fa-compass" style={{ marginRight: 4, color: '#2dce89' }} />{p.facing}</span>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }} onClick={e => e.stopPropagation()}>
            <button onClick={() => navigate(`/editproperty/${p.id}`)}
              style={{ flex: 1, padding: '7px', background: '#5e72e415', color: '#5e72e4', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <i className="fas fa-edit" /> Edit
            </button>
            <button onClick={() => navigate(`/property/${p.id}/book`)}
              style={{ flex: 1, padding: '7px', background: '#2dce8915', color: '#2dce89', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <i className="fas fa-file-contract" /> Book
            </button>
            <button onClick={() => deleteProperty(p.id)}
              style={{ padding: '7px 10px', background: '#f5365c15', color: '#f5365c', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
              <i className="fas fa-trash" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PropertyRow = ({ p }) => {
    const cat = getPropertyCategory(p.propertyCategory);
    return (
      <tr style={{ borderBottom: '1px solid #f0f2f5', cursor: 'pointer' }} onClick={() => navigate(`/property/${p.id}`)}>
        <td style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#5e72e4,#825ee4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className={cat.icon} style={{ color: '#fff', fontSize: 14 }} />
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: '#1a1f36', fontSize: 13 }}>{p.propertyName}</p>
              {p.projectName && <p style={{ margin: 0, fontSize: 11, color: '#8898aa' }}>{p.projectName}{p.unitNumber ? ` · Unit ${p.unitNumber}` : ''}</p>}
            </div>
          </div>
        </td>
        <td style={{ padding: '12px 16px', fontSize: 13, color: '#525f7f' }}>{[p.city, p.state].filter(Boolean).join(', ') || '—'}</td>
        <td style={{ padding: '12px 16px' }}>
          <span style={{ ...badge('#5e72e4', '#5e72e418'), fontSize: 12 }}>{cat.label}</span>
          {(p.inventorySource || 'Own') === 'ChannelPartner' && (
            <span style={{ ...badge('#825ee4', '#825ee424'), fontSize: 11, marginLeft: 6 }}>CP</span>
          )}
        </td>
        <td style={{ padding: '12px 16px' }} onClick={e => e.stopPropagation()}>
          <StatusDropdown property={p} />
        </td>
        <td style={{ padding: '12px 16px', fontWeight: 700, color: '#5e72e4', fontSize: 14 }}>{fmtPrice(p.price)}</td>
        <td style={{ padding: '12px 16px', fontSize: 13, color: '#8898aa' }}>{fmtSize(p.size, p.sizeUnit)}</td>
        <td style={{ padding: '12px 16px' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => navigate(`/editproperty/${p.id}`)} style={{ padding: '5px 10px', background: '#5e72e415', color: '#5e72e4', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}><i className="fas fa-edit" /></button>
            <button onClick={() => navigate(`/property/${p.id}/book`)} style={{ padding: '5px 10px', background: '#2dce8915', color: '#2dce89', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}><i className="fas fa-file-contract" /></button>
            <button onClick={() => deleteProperty(p.id)} style={{ padding: '5px 10px', background: '#f5365c15', color: '#f5365c', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}><i className="fas fa-trash" /></button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }} onClick={() => setStatusMenuId(null)}>
      <Toast ref={toast} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1f36' }}>
            <i className="fas fa-building" style={{ color: '#5e72e4', marginRight: 10 }} />Property Inventory
          </h3>
          <p style={{ margin: '4px 0 0', color: '#8898aa' }}>Manage all properties, units, bookings and inventory</p>
        </div>
        <button onClick={() => navigate('/addproperty')}
          style={{ padding: '12px 24px', background: 'linear-gradient(135deg,#5e72e4,#825ee4)', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
          <i className="fas fa-plus" style={{ marginRight: 8 }} />Add Property
        </button>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total"     val={stats?.total}        color="#5e72e4" icon="fa-layer-group" />
        <StatCard label="Available" val={stats?.available}    color="#2dce89" icon="fa-check-circle" />
        <StatCard label="Sold"      val={stats?.sold}         color="#f5365c" icon="fa-tag" />
        <StatCard label="Rented"    val={stats?.rented}       color="#11cdef" icon="fa-key" />
        <StatCard label="Reserved"  val={stats?.reserved}     color="#fb6340" icon="fa-bookmark" />
        <div style={{ ...card({ padding: 20, textAlign: 'center', borderTop: '4px solid #2dce89' }) }}>
          <i className="fas fa-rupee-sign" style={{ color: '#2dce89', fontSize: 22, marginBottom: 8 }} />
          <p style={{ margin: 0, fontSize: 11, color: '#8898aa', fontWeight: 700, textTransform: 'uppercase' }}>Revenue</p>
          <p style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 800, color: '#1a1f36' }}>{fmtPrice(stats?.totalRevenue)}</p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ ...card({ padding: '14px 18px', marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }) }}>
        <input placeholder="Search name, project, city…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 200px', padding: '8px 14px', borderRadius: 9, border: '1px solid #e3e6ec', fontSize: 13 }} />

        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid #e3e6ec', fontSize: 13, minWidth: 130 }}>
          <option value="">All Statuses</option>
          {PROPERTY_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>

        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid #e3e6ec', fontSize: 13, minWidth: 140 }}>
          <option value="">All Categories</option>
          {PROPERTY_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>

        <select value={filterInventory} onChange={e => setFilterInventory(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid #e3e6ec', fontSize: 13, minWidth: 150 }}>
          <option value="">All inventory</option>
          <option value="Own">Own inventory</option>
          <option value="ChannelPartner">Channel partner</option>
        </select>

        {projects.length > 0 && (
          <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid #e3e6ec', fontSize: 13, minWidth: 140 }}>
            <option value="">All Projects</option>
            {projects.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}

        {/* Status quick-filter chips */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PROPERTY_STATUSES.map(s => {
            const cnt = properties.filter(p => p.propertyStatus === s.id).length;
            if (!cnt) return null;
            return (
              <button key={s.id} onClick={() => setFilterStatus(filterStatus === s.id ? '' : s.id)}
                style={{ padding: '4px 12px', borderRadius: 20, border: `1px solid ${s.color}`, background: filterStatus === s.id ? s.color : 'transparent', color: filterStatus === s.id ? '#fff' : s.color, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                {s.label}: {cnt}
              </button>
            );
          })}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {['grid', 'list'].map(m => (
            <button key={m} onClick={() => setViewMode(m)}
              style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: viewMode === m ? '#5e72e4' : '#f0f2f5', color: viewMode === m ? '#fff' : '#8898aa', cursor: 'pointer', fontSize: 13 }}>
              <i className={`fas fa-${m === 'grid' ? 'th' : 'list'}`} />
            </button>
          ))}
        </div>
      </div>

      {/* Results label */}
      <p style={{ color: '#8898aa', fontSize: 13, marginBottom: 14 }}>
        Showing <strong style={{ color: '#1a1f36' }}>{filtered.length}</strong> of {properties.length} properties
      </p>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 32, color: '#5e72e4' }} />
          <p style={{ color: '#8898aa', marginTop: 12 }}>Loading properties…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card({ padding: '60px', textAlign: 'center' }) }}>
          <i className="fas fa-building" style={{ fontSize: 48, color: '#e3e6ec', marginBottom: 16 }} />
          <h5 style={{ color: '#8898aa' }}>No properties found</h5>
          <button onClick={() => navigate('/addproperty')}
            style={{ padding: '10px 24px', background: '#5e72e4', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', marginTop: 8, fontWeight: 700 }}>
            Add Your First Property
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 18 }}>
          {filtered.map(p => <PropertyCard key={p.id} p={p} />)}
        </div>
      ) : (
        <div style={card({ padding: 0, overflow: 'hidden' })}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)' }}>
                  {['Property', 'Location', 'Category', 'Status', 'Price', 'Size', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '13px 16px', textAlign: 'left', color: '#fff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => <PropertyRow key={p.id} p={p} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inventory per Project */}
      {stats?.projects?.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h5 style={{ margin: '0 0 16px', color: '#1a1f36', fontWeight: 800, fontSize: 16 }}>
            <i className="fas fa-city" style={{ color: '#825ee4', marginRight: 8 }} />Project-wise Inventory
          </h5>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
            {stats.projects.map(proj => {
              const avPct = proj.total ? Math.round((proj.available / proj.total) * 100) : 0;
              return (
                <div key={proj.projectName} style={card({ padding: 18 })}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 800, color: '#1a1f36', fontSize: 15 }}>{proj.projectName}</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#8898aa' }}>{proj.total} units total</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#2dce89' }}>{avPct}%</p>
                      <p style={{ margin: 0, fontSize: 11, color: '#8898aa' }}>available</p>
                    </div>
                  </div>
                  <div style={{ background: '#f0f2f5', borderRadius: 6, height: 8, overflow: 'hidden', marginBottom: 12 }}>
                    <div style={{ width: `${avPct}%`, height: '100%', background: 'linear-gradient(90deg,#2dce89,#11cdef)', borderRadius: 6 }} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[['Available', proj.available, '#2dce89'], ['Sold', proj.sold, '#f5365c'], ['Rented', proj.rented, '#11cdef'], ['Reserved', proj.reserved, '#fb6340']].map(([l, v, c]) =>
                      v > 0 ? <span key={l} style={{ background: `${c}18`, color: c, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{l}: {v}</span> : null
                    )}
                  </div>
                  {/* Unit colour grid */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 12 }}>
                    {proj.units?.slice(0, 30).map(u => {
                      const st = getPropertyStatus(u.propertyStatus);
                      return (
                        <div key={u.id} title={`${u.unitNumber || u.propertyName} · ${st.label}`}
                          onClick={() => navigate(`/property/${u.id}`)}
                          style={{ width: 28, height: 28, borderRadius: 6, background: st.bg, border: `2px solid ${st.color}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: st.color }}>
                          {(u.unitNumber || u.propertyName || '').slice(-3)}
                        </div>
                      );
                    })}
                    {(proj.units?.length || 0) > 30 && (
                      <div style={{ width: 28, height: 28, borderRadius: 6, background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#8898aa', fontWeight: 700 }}>
                        +{proj.units.length - 30}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, fontSize: 11, color: '#8898aa', flexWrap: 'wrap' }}>
                    {PROPERTY_STATUSES.map(s => <span key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, display: 'inline-block' }} />{s.label}</span>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
